import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { AddCircleOutlineOutlined } from '@mui/icons-material';
import { useFormik } from 'formik';
import { useAuth0 } from '@auth0/auth0-react';

import { FHInputField, FHAntdDateTimePicker, FHButton, FHSelect } from '../../../../base';
import { goalActions } from '../../../../../store';
import type { RootState } from '../../../../../store';
import { targetPhaseSchema } from '../utils/validationSchema';
import { GoalTypes } from '../../../treatment-plan/goals/old/constants';

import type {
  ITargetPhase,
  ITreatmentPlanGoal,
  ITreatmentPlanGoalTarget,
  SelectValueType,
} from '../../../../../utils/types';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';

interface AddFormProps {
  isEditable: boolean;
  onAdd: (data: ITargetPhase) => void;
}

function AddForm(props: AddFormProps) {
  const { onAdd, isEditable } = props;
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { current: clientSupervision } = useSelector(
    (state: RootState) => state.clientSupervisions,
  );
  const { list: treatmentPlanGoals } = useSelector((state: RootState) => state.goals);
  const [goalId, setGoalId] = useState<number | null>(null);
  const [goals, setGoals] = useState<ITreatmentPlanGoal[]>([]);
  const [targets, setTargets] = useState<ITreatmentPlanGoalTarget[]>([]);

  useEffect(() => {
    if (!clientSupervision?.active_treatment_id || !clientSupervision?.date_start) return;
    if (isEditable) {
      dispatch(
        goalActions.getGoals({
          treatmentPlanId: clientSupervision?.active_treatment_id,
          date: moment(clientSupervision?.date_start).utc().format(),
          getAccessTokenSilently,
        }),
      );
    }

    setGoalId(null);
    setFieldValue('target_id', null);

    return () => {
      dispatch(goalActions.clearState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSupervision?.active_treatment_id, clientSupervision?.date_start, isEditable]);

  useEffect(() => {
    if (!treatmentPlanGoals?.length) return;

    const goals = treatmentPlanGoals.filter(
      (goal: ITreatmentPlanGoal) => goal.goal_type !== GoalTypes.Caregiver && goal.is_active,
    );
    setGoals(goals);
  }, [treatmentPlanGoals]);

  useEffect(() => {
    if (!goalId || !goals) {
      setTargets([]);
      return;
    }

    const goal = goals.find((goal) => goal.id === goalId);
    setTargets(goal?.targets?.filter((target) => target?.is_active) || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId]);

  const formik = useFormik<ITargetPhase>({
    initialValues: {
      target_id: '',
      title: '',
      date_added: moment(new Date()).format('YYYY-MM-DD'),
    },
    onSubmit: async (values) => {
      onAdd(values);
      resetForm();
      setGoalId(null);
    },
    validationSchema: targetPhaseSchema,
  });

  const { values, setFieldValue, resetForm } = formik;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexBasis: '40%' }}>
            <Typography variant="h4" fontWeight={500} textAlign="left" sx={{ mr: 1 }} noWrap>
              Goal:
            </Typography>
            <FHSelect
              data-testid="targetPhaseGoalDropDown"
              sx={{ minWidth: 200, flex: 1, boxShadow: 'none' }}
              inputHeight={40}
              disabled={!isEditable}
              options={goals
                .filter((goal): goal is ITreatmentPlanGoal & { id: number } => goal.id !== undefined)
                .map((goal) => ({
                  value: goal.id,
                  label: goal.goal_name?.label ?? '',
                }))}
              value={goalId || ''}
              onUpdate={(value: SelectValueType) => {
                setGoalId(goals.find((goal) => goal.id === value)?.id ?? null);
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, ml: 3 }}>
            <Typography variant="h4" fontWeight={500} textAlign="left" sx={{ mr: 1 }} noWrap>
              Target/Replacement behavior:
            </Typography>
            <FHSelect
              data-testid="targetReplaceMentBehaviourDropdown"
              sx={{ minWidth: 200, flex: 1, boxShadow: 'none' }}
              inputHeight={40}
              disabled={!isEditable}
              options={targets
                .filter(
                  (target): target is ITreatmentPlanGoalTarget & { id: number } =>
                    target.id !== undefined,
                )
                .map((target) => ({
                  value: target.id,
                  label: target.label,
                }))}
              value={values.target_id || ''}
              onUpdate={(value: SelectValueType) => {
                setFieldValue('target_id', targets.find((target) => target.id === value)?.id ?? '');
              }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }} mt={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexBasis: '60%' }}>
            <Typography variant="h4" fontWeight={500} textAlign="left" sx={{ mr: 1 }} noWrap>
              Title:
            </Typography>
            <FHInputField
              data-testid="targetPhaseTitle"
              sx={{ minWidth: 200, flex: 1, boxShadow: 'none' }}
              inputHeight={40}
              disabled={!isEditable}
              value={values.title}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setFieldValue('title', e.target.value)
              }
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h4" fontWeight={500} textAlign="left" sx={{ mr: 1 }} noWrap>
              Date:
            </Typography>
            <FHAntdDateTimePicker
              data-testid="FAndDateTimePicker"
              format="MM/DD/YYYY"
              sx={{ boxShadow: 'none' }}
              allowClear={false}
              inputHeight={40}
              disabled={!isEditable}
              value={moment(values.date_added)}
              onChange={(value: Moment) => setFieldValue('date_added', value.format('YYYY-MM-DD'))}
              onSelect={(value: Moment) => setFieldValue('date_added', value.format('YYYY-MM-DD'))}
            />
          </Box>
          <FHButton
            data-testid="addTargetPhaseChangeLine"
            sx={{ minWidth: 190 }}
            startIcon={<AddCircleOutlineOutlined />}
            disabled={!(formik.isValid && formik.dirty) || !isEditable}
            type="submit"
          >
            Add
          </FHButton>
        </Box>
      </form>
    </Box>
  );
}

export default AddForm;
